class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);


/* ============================================================
   VF / Pebble & Wick — Cinematic Search Form Enhancements
   Safe layer on top of Dawn search-form.js.
   Keeps reset behavior, debounce and predictive-search inheritance intact.
   ============================================================ */

(() => {
  const SearchFormElement = customElements.get('search-form');
  if (!SearchFormElement || SearchFormElement.prototype.__vfCinematicPatched) return;

  SearchFormElement.prototype.__vfCinematicPatched = true;

  const originalToggleResetButton = SearchFormElement.prototype.toggleResetButton;
  const originalOnChange = SearchFormElement.prototype.onChange;
  const originalOnFormReset = SearchFormElement.prototype.onFormReset;

  const root = document.documentElement;

  const getQuery = (searchForm) => searchForm.input?.value?.trim() || '';

  const setSearchFormState = (searchForm, state, detail = {}) => {
    searchForm.dataset.vfSearchFormState = state;

    searchForm.classList.toggle('vf-search-form-empty', state === 'empty');
    searchForm.classList.toggle('vf-search-form-filled', state === 'filled');
    searchForm.classList.toggle('vf-search-form-focused', state === 'focused');
    searchForm.classList.toggle('vf-search-form-resetting', state === 'resetting');

    root.classList.toggle('vf-search-form-has-value', getQuery(searchForm).length > 0);

    searchForm.dispatchEvent(
      new CustomEvent(`vf:search-form-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const enhanceSearchForm = (searchForm) => {
    if (!searchForm || searchForm.__vfCinematicEnhanced) return;
    searchForm.__vfCinematicEnhanced = true;

    searchForm.classList.add('vf-search-form-enhanced');

    const input = searchForm.input || searchForm.querySelector('input[type="search"]');
    const resetButton = searchForm.resetButton || searchForm.querySelector('button[type="reset"]');

    if (input) {
      input.classList.add('vf-search-form__input');
      input.setAttribute('autocomplete', input.getAttribute('autocomplete') || 'off');

      input.addEventListener('focus', () => {
        setSearchFormState(searchForm, 'focused', {
          query: getQuery(searchForm)
        });
        root.classList.add('vf-search-input-focused');
      });

      input.addEventListener('blur', () => {
        window.setTimeout(() => {
          if (searchForm.contains(document.activeElement)) return;

          searchForm.classList.remove('vf-search-form-focused');
          root.classList.remove('vf-search-input-focused');

          setSearchFormState(searchForm, getQuery(searchForm).length ? 'filled' : 'empty', {
            query: getQuery(searchForm)
          });
        }, 0);
      });
    }

    if (resetButton) {
      resetButton.classList.add('vf-search-form__reset');
      resetButton.setAttribute('aria-label', resetButton.getAttribute('aria-label') || window.accessibilityStrings?.close || 'Clear search');
    }

    setSearchFormState(searchForm, getQuery(searchForm).length ? 'filled' : 'empty', {
      query: getQuery(searchForm)
    });
  };

  SearchFormElement.prototype.toggleResetButton = function vfToggleResetButton() {
    const result = originalToggleResetButton.call(this);
    const query = getQuery(this);

    this.classList.toggle('vf-search-form-has-reset', Boolean(this.resetButton && !this.resetButton.classList.contains('hidden')));
    setSearchFormState(this, query.length ? 'filled' : 'empty', { query });

    return result;
  };

  SearchFormElement.prototype.onChange = function vfOnChange(event) {
    const result = originalOnChange.call(this, event);
    const query = getQuery(this);

    this.dataset.vfQueryLength = String(query.length);
    this.classList.add('vf-search-form-changing');

    this.dispatchEvent(
      new CustomEvent('vf:search-form-changed', {
        bubbles: true,
        detail: {
          query,
          length: query.length
        }
      })
    );

    window.clearTimeout(this.__vfSearchChangeTimeout);
    this.__vfSearchChangeTimeout = window.setTimeout(() => {
      this.classList.remove('vf-search-form-changing');
    }, 420);

    return result;
  };

  SearchFormElement.prototype.onFormReset = function vfOnFormReset(event) {
    const beforeQuery = getQuery(this);

    setSearchFormState(this, 'resetting', {
      query: beforeQuery
    });

    const result = originalOnFormReset.call(this, event);

    window.requestAnimationFrame(() => {
      const afterQuery = getQuery(this);

      setSearchFormState(this, afterQuery.length ? 'filled' : 'empty', {
        query: afterQuery,
        previousQuery: beforeQuery
      });

      this.dispatchEvent(
        new CustomEvent('vf:search-form-reset', {
          bubbles: true,
          detail: {
            previousQuery: beforeQuery,
            query: afterQuery
          }
        })
      );
    });

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('search-form').forEach(enhanceSearchForm);
  }, { once: true });

  const searchFormObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('search-form')) {
          enhanceSearchForm(node);
        }

        node.querySelectorAll?.('search-form').forEach(enhanceSearchForm);
      });
    });
  });

  if (document.body) {
    searchFormObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
